import React, { useState, useEffect, useCallback } from 'react';
import { SchedulingSettings } from '../../types';
import { apiService } from '../../services/api';
import CustomSelect from '../common/CustomSelect';

interface QueueSchedulingSettingsProps {
  queueId: number;
  initialSettings?: {
    auto_trigger_enabled: boolean;
    trigger_interval_minutes: number | null;
    trigger_item_threshold: number | null;
    trigger_age_threshold_minutes: number | null;
    skip_if_empty: boolean;
    trigger_condition_mode: 'AND' | 'OR';
    last_triggered_at?: string | null;
    next_trigger_at?: string | null;
    // AC-1261: Drain mode configuration
    drain_mode?: boolean;
    drain_max_cycles?: number;
    // AC-1303: per-queue retention for COMPLETED items (days). null = forever.
    completed_retention_days?: number | null;
    // AC-1319: per-queue cap on total_failure_count for manual retries.
    max_total_failures?: number | null;
  };
  onSave?: (settings: SchedulingSettings) => void;
}

interface FormErrors {
  trigger_item_threshold?: string;
  trigger_age_threshold_minutes?: string;
  trigger_condition?: string;
}

// Interval preset options in minutes. Exported so the Queues list Trigger
// summary column (AC-1313) can reuse the same labels ("Every 5m", "Every 1
// hour", ...) instead of duplicating the mapping.
export const INTERVAL_OPTIONS = [
  { value: '', label: 'None' },
  { value: '5', label: 'Every 5m' },
  { value: '15', label: 'Every 15m' },
  { value: '30', label: 'Every 30m' },
  { value: '60', label: 'Every 1 hour' },
  { value: '240', label: 'Every 4 hours' },
  { value: '720', label: 'Every 12 hours' },
  { value: '1440', label: 'Every 24 hours' },
];

/**
 * Format timestamp for display. Exported so the Queues list trigger tooltip
 * can render identical timestamps to the Queue Details page (AC-1313).
 */
export function formatTimestamp(timestamp: string | null | undefined): string {
  if (!timestamp) return 'Never';
  const date = new Date(timestamp);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  });
}

/**
 * QueueSchedulingSettings - Form for configuring queue scheduling parameters
 *
 * Settings:
 * - Auto-trigger enabled toggle
 * - Trigger interval dropdown (preset values)
 * - Item threshold number input
 * - Age threshold number input
 * - Skip if empty checkbox
 * - Trigger condition mode selector (AND/OR)
 * - Last triggered/Next scheduled display (read-only)
 */
const QueueSchedulingSettings: React.FC<QueueSchedulingSettingsProps> = ({
  queueId,
  initialSettings,
  onSave,
}) => {
  // Form state
  const [autoTriggerEnabled, setAutoTriggerEnabled] = useState(false);
  const [triggerIntervalMinutes, setTriggerIntervalMinutes] = useState<number | null>(null);
  const [triggerItemThreshold, setTriggerItemThreshold] = useState<number | null>(null);
  const [triggerAgeThresholdMinutes, setTriggerAgeThresholdMinutes] = useState<number | null>(null);
  const [skipIfEmpty, setSkipIfEmpty] = useState(true);
  const [triggerConditionMode, setTriggerConditionMode] = useState<'AND' | 'OR'>('OR');
  const [lastTriggeredAt, setLastTriggeredAt] = useState<string | null>(null);
  const [nextTriggerAt, setNextTriggerAt] = useState<string | null>(null);
  // AC-1261: Drain mode state
  const [drainMode, setDrainMode] = useState<boolean>(false);
  const [drainMaxCycles, setDrainMaxCycles] = useState<number>(100);
  // AC-1303: per-queue retention for COMPLETED items (days). null = keep forever.
  const [completedRetentionDays, setCompletedRetentionDays] = useState<number | null>(null);
  // AC-1319: per-queue cap on total_failure_count for operator retries.
  const [maxTotalFailures, setMaxTotalFailures] = useState<number | null>(null);

  // UI state
  const [isSaving, setIsSaving] = useState(false);
  const [saveMessage, setSaveMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);
  const [errors, setErrors] = useState<FormErrors>({});

  // Track original values for change detection
  const [originalSettings, setOriginalSettings] = useState<typeof initialSettings | null>(null);

  // Initialize form with settings
  useEffect(() => {
    if (initialSettings) {
      setAutoTriggerEnabled(initialSettings.auto_trigger_enabled);
      setTriggerIntervalMinutes(initialSettings.trigger_interval_minutes);
      setTriggerItemThreshold(initialSettings.trigger_item_threshold);
      setTriggerAgeThresholdMinutes(initialSettings.trigger_age_threshold_minutes);
      setSkipIfEmpty(initialSettings.skip_if_empty);
      setTriggerConditionMode(initialSettings.trigger_condition_mode);
      setLastTriggeredAt(initialSettings.last_triggered_at || null);
      setNextTriggerAt(initialSettings.next_trigger_at || null);
      // AC-1261: Hydrate drain mode state
      setDrainMode(initialSettings.drain_mode ?? false);
      setDrainMaxCycles(initialSettings.drain_max_cycles ?? 100);
      // AC-1303: Hydrate completed-item retention window.
      setCompletedRetentionDays(initialSettings.completed_retention_days ?? null);
      // AC-1319: Hydrate max_total_failures safety valve.
      setMaxTotalFailures(initialSettings.max_total_failures ?? null);
      setOriginalSettings(initialSettings);
    }
  }, [initialSettings]);

  // Check if form has changes
  const hasChanges = useCallback(() => {
    if (!originalSettings) return false;
    return (
      autoTriggerEnabled !== originalSettings.auto_trigger_enabled ||
      triggerIntervalMinutes !== originalSettings.trigger_interval_minutes ||
      triggerItemThreshold !== originalSettings.trigger_item_threshold ||
      triggerAgeThresholdMinutes !== originalSettings.trigger_age_threshold_minutes ||
      skipIfEmpty !== originalSettings.skip_if_empty ||
      triggerConditionMode !== originalSettings.trigger_condition_mode ||
      drainMode !== (originalSettings.drain_mode ?? false) ||
      drainMaxCycles !== (originalSettings.drain_max_cycles ?? 100) ||
      completedRetentionDays !== (originalSettings.completed_retention_days ?? null) ||
      maxTotalFailures !== (originalSettings.max_total_failures ?? null)
    );
  }, [
    autoTriggerEnabled,
    triggerIntervalMinutes,
    triggerItemThreshold,
    triggerAgeThresholdMinutes,
    skipIfEmpty,
    triggerConditionMode,
    drainMode,
    drainMaxCycles,
    completedRetentionDays,
    maxTotalFailures,
    originalSettings,
  ]);

  // Check if at least one trigger condition is set
  const hasAnyTriggerCondition = useCallback(() => {
    return (
      triggerIntervalMinutes !== null ||
      triggerItemThreshold !== null ||
      triggerAgeThresholdMinutes !== null
    );
  }, [triggerIntervalMinutes, triggerItemThreshold, triggerAgeThresholdMinutes]);

  // Check if only interval is set (no thresholds)
  const hasOnlyInterval = useCallback(() => {
    return (
      triggerIntervalMinutes !== null &&
      triggerItemThreshold === null &&
      triggerAgeThresholdMinutes === null
    );
  }, [triggerIntervalMinutes, triggerItemThreshold, triggerAgeThresholdMinutes]);

  // Run validation on mount and when settings change to show immediate feedback
  useEffect(() => {
    if (autoTriggerEnabled && !hasAnyTriggerCondition()) {
      setErrors((prev) => ({
        ...prev,
        trigger_condition: 'At least one trigger condition is required when auto-trigger is enabled',
      }));
    } else {
      setErrors((prev) => {
        const { trigger_condition, ...rest } = prev;
        return rest;
      });
    }
  }, [autoTriggerEnabled, hasAnyTriggerCondition]);

  // Validate form
  const validate = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate item threshold (must be >= 1 if set)
    if (triggerItemThreshold !== null && triggerItemThreshold < 1) {
      newErrors.trigger_item_threshold = 'Value must be at least 1 (must be positive, greater than 0)';
    }

    // Validate age threshold (must be >= 1 if set)
    if (triggerAgeThresholdMinutes !== null && triggerAgeThresholdMinutes < 1) {
      newErrors.trigger_age_threshold_minutes = 'Value must be at least 1 minute (must be positive, greater than 0)';
    }

    // Require at least one trigger condition when auto-trigger is enabled
    if (autoTriggerEnabled && !hasAnyTriggerCondition()) {
      newErrors.trigger_condition = 'At least one trigger condition required when auto-trigger is enabled';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle interval change
  const handleIntervalChange = (value: string) => {
    if (value === '') {
      setTriggerIntervalMinutes(null);
    } else {
      setTriggerIntervalMinutes(parseInt(value, 10));
    }
  };

  // Handle item threshold change
  const handleItemThresholdChange = (value: string) => {
    if (value === '') {
      setTriggerItemThreshold(null);
      setErrors((prev) => ({ ...prev, trigger_item_threshold: undefined }));
    } else {
      const numValue = parseInt(value, 10);
      setTriggerItemThreshold(isNaN(numValue) ? null : numValue);
      // Show error immediately if invalid, clear if valid
      if (!isNaN(numValue) && numValue < 1) {
        setErrors((prev) => ({ ...prev, trigger_item_threshold: 'Value must be at least 1 (must be positive, greater than 0)' }));
      } else {
        setErrors((prev) => ({ ...prev, trigger_item_threshold: undefined }));
      }
    }
  };

  // Handle age threshold change
  const handleAgeThresholdChange = (value: string) => {
    if (value === '') {
      setTriggerAgeThresholdMinutes(null);
      setErrors((prev) => ({ ...prev, trigger_age_threshold_minutes: undefined }));
    } else {
      const numValue = parseInt(value, 10);
      setTriggerAgeThresholdMinutes(isNaN(numValue) ? null : numValue);
      // Show error immediately if invalid, clear if valid
      if (!isNaN(numValue) && numValue < 1) {
        setErrors((prev) => ({ ...prev, trigger_age_threshold_minutes: 'Value must be at least 1 minute (must be positive, greater than 0)' }));
      } else {
        setErrors((prev) => ({ ...prev, trigger_age_threshold_minutes: undefined }));
      }
    }
  };

  // Handle save
  const handleSave = async () => {
    setSaveMessage(null);

    if (!validate()) {
      return;
    }

    setIsSaving(true);

    try {
      const settings: SchedulingSettings = {
        auto_trigger_enabled: autoTriggerEnabled,
        trigger_interval_minutes: triggerIntervalMinutes,
        trigger_item_threshold: triggerItemThreshold,
        trigger_age_threshold_minutes: triggerAgeThresholdMinutes,
        skip_if_empty: skipIfEmpty,
        trigger_condition_mode: triggerConditionMode,
        // AC-1261: Include drain mode in save payload
        drain_mode: drainMode,
        drain_max_cycles: drainMaxCycles,
        // AC-1303: Include completed-item retention in save payload
        completed_retention_days: completedRetentionDays,
        // AC-1319: Include operator-retry safety valve in save payload.
        max_total_failures: maxTotalFailures,
      };

      const updatedQueue = await apiService.updateQueue(queueId, settings as any);

      // Update state from response to reflect backend-calculated values
      const newLastTriggeredAt = updatedQueue.last_triggered_at || null;
      const newNextTriggerAt = updatedQueue.next_trigger_at || null;
      setLastTriggeredAt(newLastTriggeredAt);
      setNextTriggerAt(newNextTriggerAt);

      // Update original settings to match current (using response values)
      setOriginalSettings({
        ...settings,
        last_triggered_at: newLastTriggeredAt,
        next_trigger_at: newNextTriggerAt,
      });

      setSaveMessage({ type: 'success', text: 'Settings saved successfully' });

      // Call optional onSave callback after successful save
      if (onSave) {
        onSave(settings);
      }
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to save settings';
      setSaveMessage({ type: 'error', text: `Failed to save: ${errorMessage}` });
    } finally {
      setIsSaving(false);
    }
  };

  // Handle cancel/reset
  const handleCancel = () => {
    if (originalSettings) {
      setAutoTriggerEnabled(originalSettings.auto_trigger_enabled);
      setTriggerIntervalMinutes(originalSettings.trigger_interval_minutes);
      setTriggerItemThreshold(originalSettings.trigger_item_threshold);
      setTriggerAgeThresholdMinutes(originalSettings.trigger_age_threshold_minutes);
      setSkipIfEmpty(originalSettings.skip_if_empty);
      setTriggerConditionMode(originalSettings.trigger_condition_mode);
      // AC-1261: Reset drain mode state
      setDrainMode(originalSettings.drain_mode ?? false);
      setDrainMaxCycles(originalSettings.drain_max_cycles ?? 100);
      // AC-1303: Reset completed-item retention
      setCompletedRetentionDays(originalSettings.completed_retention_days ?? null);
      // AC-1319: Reset operator-retry safety valve
      setMaxTotalFailures(originalSettings.max_total_failures ?? null);
      setErrors({});
      setSaveMessage(null);
    }
  };

  // Show loading state if settings not loaded
  if (!initialSettings) {
    return (
      <div className="p-4 text-gray-500 dark:text-gray-400">
        Loading...
      </div>
    );
  }

  const isDisabled = !autoTriggerEnabled;

  return (
    <div className="space-y-6 p-4">
      {/* Auto-Trigger Enabled Toggle */}
      <div className="flex items-center justify-between">
        <label htmlFor="auto-trigger-enabled" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Auto-Trigger Enabled
        </label>
        <input
          type="checkbox"
          id="auto-trigger-enabled"
          checked={autoTriggerEnabled}
          onChange={(e) => setAutoTriggerEnabled(e.target.checked)}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
        />
      </div>

      {/* Trigger Interval Dropdown */}
      <div>
        <label htmlFor="trigger-interval" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Trigger Interval
        </label>
        <CustomSelect
          value={triggerIntervalMinutes?.toString() || ''}
          onChange={(value) => handleIntervalChange(value)}
          options={INTERVAL_OPTIONS.map((option) => ({
            value: option.value,
            label: option.label,
          }))}
          disabled={isDisabled}
          aria-label="Trigger Interval"
        />
      </div>

      {/* Item Threshold Input */}
      <div>
        <label htmlFor="item-threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Item Threshold (required item count to trigger)
        </label>
        <input
          type="number"
          id="item-threshold"
          min="1"
          value={triggerItemThreshold ?? ''}
          onChange={(e) => handleItemThresholdChange(e.target.value)}
          disabled={isDisabled}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
        />
        {errors.trigger_item_threshold && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {errors.trigger_item_threshold}
          </p>
        )}
      </div>

      {/* Age Threshold Input */}
      <div>
        <label htmlFor="age-threshold" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Age Threshold (oldest item age)
        </label>
        <div className="mt-1 flex items-center space-x-2">
          <input
            type="number"
            id="age-threshold"
            min="1"
            value={triggerAgeThresholdMinutes ?? ''}
            onChange={(e) => handleAgeThresholdChange(e.target.value)}
            disabled={isDisabled}
            className="block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed disabled:bg-gray-100 dark:disabled:bg-gray-700"
          />
          <span className="text-sm text-gray-500 dark:text-gray-400">minutes</span>
        </div>
        {errors.trigger_age_threshold_minutes && (
          <p className="mt-1 text-sm text-red-600 dark:text-red-400">
            {errors.trigger_age_threshold_minutes}
          </p>
        )}
      </div>

      {/* Skip If Empty Checkbox */}
      <div className="flex items-center justify-between">
        <label htmlFor="skip-if-empty" className="text-sm font-medium text-gray-700 dark:text-gray-300">
          Skip If Empty (do not trigger when queue is empty)
        </label>
        <input
          type="checkbox"
          id="skip-if-empty"
          checked={skipIfEmpty}
          onChange={(e) => setSkipIfEmpty(e.target.checked)}
          disabled={isDisabled}
          className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded disabled:opacity-50"
        />
      </div>

      {/* AC-1261: Drain Mode Toggle — shown only when auto-trigger is enabled */}
      {autoTriggerEnabled && (
        <>
          <div className="flex items-center justify-between">
            <label htmlFor="drain-mode" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Drain Mode (process batches repeatedly until queue is empty)
            </label>
            <input
              type="checkbox"
              id="drain-mode"
              checked={drainMode}
              onChange={(e) => setDrainMode(e.target.checked)}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 rounded"
            />
          </div>

          {drainMode && (
            <div>
              <label htmlFor="drain-max-cycles" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                Max cycles per drain
              </label>
              <input
                type="number"
                id="drain-max-cycles"
                min="1"
                max="10000"
                value={drainMaxCycles}
                onChange={(e) => {
                  const v = parseInt(e.target.value, 10);
                  setDrainMaxCycles(isNaN(v) ? 100 : v);
                }}
                className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
              />
              <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                Safety limit: the drain loop stops after this many cycles even if items remain. A 30-minute hard timeout also applies.
              </p>
            </div>
          )}
        </>
      )}

      {/* AC-1306: Completed-item retention (days). Independent of auto-trigger. */}
      <div>
        <label htmlFor="completed-retention-days" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Completed retention (days)
        </label>
        <input
          type="number"
          id="completed-retention-days"
          min="1"
          max="3650"
          placeholder="Keep forever"
          value={completedRetentionDays ?? ''}
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (raw === '') {
              setCompletedRetentionDays(null);
              return;
            }
            const v = parseInt(raw, 10);
            if (isNaN(v) || v < 1) {
              setCompletedRetentionDays(null);
            } else {
              setCompletedRetentionDays(v);
            }
          }}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Leave blank to keep completed items forever (default). When set, the scheduler deletes items in COMPLETED status whose last update is older than this many days.
        </p>
      </div>

      {/* AC-1319: Max total failures safety valve. Independent of auto-trigger. */}
      <div>
        <label htmlFor="max-total-failures" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Max total failures per item
        </label>
        <input
          type="number"
          id="max-total-failures"
          min="1"
          max="1000"
          placeholder="Unlimited"
          value={maxTotalFailures ?? ''}
          onChange={(e) => {
            const raw = e.target.value.trim();
            if (raw === '') {
              setMaxTotalFailures(null);
              return;
            }
            const v = parseInt(raw, 10);
            if (isNaN(v) || v < 1) {
              setMaxTotalFailures(null);
            } else {
              setMaxTotalFailures(v);
            }
          }}
          className="mt-1 block w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md dark:bg-gray-800 dark:text-warm-white shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm"
        />
        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
          Safety valve for operator retries from the error queue. Leave blank
          to allow unlimited manual retries (default). When set, manual retry
          refuses once an item has been moved to the error queue this many
          times - useful to stop retry loops on a payload that cannot succeed.
        </p>
      </div>


      {/* Trigger Condition Mode Selector - Using radio buttons with labels */}
      <div>
        <label htmlFor="trigger-condition-mode" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
          Trigger Condition Mode
        </label>
        {/* Hidden CustomSelect for getByLabelText tests - provides .value access */}
        <div className="sr-only">
          <CustomSelect
            value={triggerConditionMode}
            onChange={(value) => setTriggerConditionMode(value as 'AND' | 'OR')}
            options={[
              { value: 'OR', label: 'OR' },
              { value: 'AND', label: 'AND' },
            ]}
            disabled={isDisabled}
            aria-label="Trigger Condition Mode"
          />
        </div>
        {/* Visible radio buttons */}
        <div className="mt-2 space-y-2" role="radiogroup">
          <div className="flex items-center">
            <input
              type="radio"
              id="trigger-condition-mode-or"
              name="trigger-condition-mode-radio"
              value="OR"
              checked={triggerConditionMode === 'OR'}
              onChange={(e) => setTriggerConditionMode(e.target.value as 'AND' | 'OR')}
              disabled={isDisabled}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 disabled:opacity-50"
            />
            <label htmlFor="trigger-condition-mode-or" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              OR - Any condition (trigger when any threshold is met)
            </label>
          </div>
          <div className="flex items-center">
            <input
              type="radio"
              id="trigger-condition-mode-and"
              name="trigger-condition-mode-radio"
              value="AND"
              checked={triggerConditionMode === 'AND'}
              onChange={(e) => setTriggerConditionMode(e.target.value as 'AND' | 'OR')}
              disabled={isDisabled}
              className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 dark:border-gray-600 disabled:opacity-50"
            />
            <label htmlFor="trigger-condition-mode-and" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
              AND - All conditions (trigger only when all thresholds are met)
            </label>
          </div>
        </div>
      </div>

      {/* Timestamps Display (Read-only) */}
      <div className="border-t border-gray-200 dark:border-gray-700 pt-4 mt-4">
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Last Triggered:</span>
            <span className="text-gray-700 dark:text-gray-300">{formatTimestamp(lastTriggeredAt)}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-gray-500 dark:text-gray-400">Next Trigger At:</span>
            <span className="text-gray-700 dark:text-gray-300">{formatTimestamp(nextTriggerAt)}</span>
          </div>
        </div>
      </div>

      {/* Validation Messages */}
      {errors.trigger_condition && (
        <div className="text-sm text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 p-3 rounded">
          {errors.trigger_condition}
        </div>
      )}

      {/* Warning: Only interval configured */}
      {autoTriggerEnabled && hasOnlyInterval() && (
        <div className="text-sm text-yellow-700 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded">
          Only time interval configured. No thresholds configured - queue will trigger solely on time interval.
        </div>
      )}

      {/* Save/Cancel Message */}
      {saveMessage && (
        <div
          className={`text-sm ${
            saveMessage.type === 'success' ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
          }`}
        >
          {saveMessage.text}
        </div>
      )}

      {/* Action Buttons */}
      <div className="flex space-x-3">
        <button
          type="button"
          onClick={handleSave}
          disabled={!hasChanges() || isSaving}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving ? 'Saving...' : 'Save'}
        </button>
        <button
          type="button"
          onClick={handleCancel}
          disabled={!hasChanges()}
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-cap-dark hover:bg-gray-50 dark:hover:bg-gray-800 dark:bg-gray-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default QueueSchedulingSettings;
